const ORDER_STATUS = {
  INITIALIZED: "initialized",
  FINERPERRORED: "finerperrored",
  FINALIZED: "finalized",
  CANCELLED: "cancelled",
};

const MSP_ORDER_STATUS = {
  UNKNOWN: "unknown",
  INITIALIZED: "initialized", // The customer has initiated a transaction.
  COMPLETED: "completed", // The customer has successfully completed the transaction.
  VOID: "void", // The transaction has been cancelled.
  EXPIRED: "expired", // The customer didn't complete payment and the transaction expired.
  DECLINED: "declined", // The issuer or acquirer has declined the transaction.
  UNCLEARED: "uncleared", // Manually capture or decline the transaction.
  SHIPPED: "shipped", // The order has been shipped and the funds are still pending.
};

const MSP_FINANCIAL_STATUS = {
  UNKNOWN: "unknown",
  INITIALIZED: "initialized", // The customer has initiated a transaction.
  COMPLETED: "completed", // The customer has successfully completed the transaction.
  VOID: "void", // The transaction has been cancelled.
  EXPIRED: "expired", // The customer didn't complete payment and the transaction expired.
  DECLINED: "declined", // The issuer or acquirer has declined the transaction.
};

const PAYMENT_STATUS = {
  PENDING: "pending",
  AWAITING_INVOICE: "awaiting_invoice",
  AWAITING_BANK_TRANSFER: "awaiting_bank_transfer",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
};

const PROCESSING_STATUS = {
  PENDING: "pending",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
};

const PAYMENT_METHODS = {
  BANK_TRANSFER: {
    id: "BANK_TRANSFER",
    description: "Bank transfer",
    logoUrl: "",
    icon: "money-bill-transfer",
    requiresMSP: false,
    index: 0,
  },
  INVOICE: {
    id: "INVOICE",
    description: "Invoice",
    logoUrl: "",
    icon: "file-invoice",
    requiresMSP: false,
    index: 1,
  },
  MISTERCASH: {
    id: "MISTERCASH",
    description: "Bancontact",
    logoUrl:
      "https://github.com/MultiSafepay/MultiSafepay-icons/blob/master/methods/bancontact.png?raw=true",
    requiresMSP: true,
    index: 2,
  },
  KBC: {
    id: "KBC",
    description: "KBC",
    logoUrl:
      "https://github.com/MultiSafepay/MultiSafepay-icons/blob/master/methods/kbc.png?raw=true",
    requiresMSP: true,
    index: 3,
  },
  CBC: {
    id: "CBC",
    description: "CBC",
    logoUrl:
      "https://github.com/MultiSafepay/MultiSafepay-icons/blob/master/methods/cbc.png?raw=true",
    requiresMSP: true,
    index: 4,
  },
  IDEAL: {
    id: "IDEAL",
    description: "iDEAL",
    logoUrl:
      "https://github.com/MultiSafepay/MultiSafepay-icons/blob/master/methods/ideal.png?raw=true",
    requiresMSP: true,
    index: 5,
  },
  MASTERCARD: {
    id: "MASTERCARD",
    description: "MasterCard",
    logoUrl:
      "https://github.com/MultiSafepay/MultiSafepay-icons/blob/master/methods/mastercard.png?raw=true",
    requiresMSP: true,
    index: 6,
  },
  VISA: {
    id: "VISA",
    description: "Visa",
    logoUrl:
      "https://github.com/MultiSafepay/MultiSafepay-icons/blob/master/methods/visa.png?raw=true",
    requiresMSP: true,
    index: 7,
  },
  MAESTRO: {
    id: "MAESTRO",
    description: "Maestro",
    logoUrl:
      "https://github.com/MultiSafepay/MultiSafepay-icons/blob/master/methods/maestro.png?raw=true",
    requiresMSP: true,
    index: 8,
  },
  PAYPAL: {
    id: "PAYPAL",
    description: "PayPal",
    logoUrl:
      "https://github.com/MultiSafepay/MultiSafepay-icons/blob/master/methods/paypal.png?raw=true",
    requiresMSP: true,
    index: 9,
  },

  GOOGLEPAY: {
    id: "GOOGLEPAY",
    description: "Google Pay",
    logoUrl:
      "https://github.com/MultiSafepay/MultiSafepay-icons/blob/master/methods/googlepay.png?raw=true",
    requiresMSP: true,
    index: 10,
  },

  APPLEPAY: {
    id: "APPLEPAY",
    description: "Apple Pay",
    logoUrl:
      "https://github.com/MultiSafepay/MultiSafepay-icons/blob/master/methods/applepay.png?raw=true",
    requiresMSP: true,
    index: 11,
  },
  BELFIUS: {
    id: "BELFIUS",
    description: "Belfius",
    logoUrl:
      "https://github.com/MultiSafepay/MultiSafepay-icons/blob/master/methods/belfius.png?raw=true",
    requiresMSP: true,
    index: 12,
  },
};

const PAYMENT_CONDITION = {
  INVOICE: "invoice",
  CASH: "cash",
};

/** Conservative defaults when invoice-payment conditions cannot be resolved. */
const PAYMENT_CONDITION_DEFAULTS = {
  canPayByInvoice: false,
  canPayByBankTransfer: false,
  isEcomActive: false,
  clientHasCustomerId: false,
  clientIsAtleastMember: false,
  clientHasToPayCash: true,
};

const normalizePaymentConditions = (conditions = {}) => {
  const merged = { ...PAYMENT_CONDITION_DEFAULTS, ...conditions };
  return {
    canPayByInvoice: merged.canPayByInvoice === true,
    canPayByBankTransfer: merged.canPayByBankTransfer === true,
    isEcomActive: merged.isEcomActive === true,
    clientHasCustomerId: merged.clientHasCustomerId === true,
    clientIsAtleastMember: merged.clientIsAtleastMember === true,
    clientHasToPayCash: merged.clientHasToPayCash === true,
  };
};

/**
 * True when the document's payment condition requires payment before/at delivery
 * (Kontant/contant or tegen voorafbetaling). Term-based conditions (e.g. 30 dagen)
 * should not get "Niet betaald" style warnings.
 */
const requiresImmediatePayment = ({ BTLCONTANT, BTLCOND } = {}) => {
  if (BTLCONTANT === true || BTLCONTANT === 1 || BTLCONTANT === "1") {
    return true;
  }
  if (typeof BTLCOND !== "string") return false;
  const cond = BTLCOND.trim().toLowerCase();
  if (!cond) return false;
  if (
    cond === "tegen voorafbetaling" ||
    cond === "kontant" ||
    cond === "contant"
  ) {
    return true;
  }
  // FinERP variants / longer labels
  return (
    cond.includes("voorafbetaling") ||
    cond.includes("kontant") ||
    cond.includes("contant")
  );
};

module.exports = {
  MSP_ORDER_STATUS,
  ORDER_STATUS,
  MSP_FINANCIAL_STATUS,
  PAYMENT_STATUS,
  PROCESSING_STATUS,
  PAYMENT_METHODS,
  PAYMENT_CONDITION,
  PAYMENT_CONDITION_DEFAULTS,
  normalizePaymentConditions,
  requiresImmediatePayment,
};
